import { useEffect, useRef } from 'react';
import type { StreamChat } from 'stream-chat';

import type { ChannelId, InboxItem } from '../fixtures/domain.js';
import { useInboxList } from '../data/inbox.js';
import { isJobie } from '../fixtures/rules.js';
import { EmptyState } from '../components/EmptyState.js';
import { Icon } from '../components/Icon.js';
import { ChannelPreview } from './ChannelPreview.js';
import { emptyQuery, withFilter, type InboxQuery } from './query.js';
import styles from './ListPanel.module.css';

/**
 * Marketing threads, identified by the channel's source in the read model —
 * replacing the member-id suffix guess (`/_(ajt|rb|mk)$/`) this rail used
 * before the backend carried a `kind`.
 */
const INFO_QUERY: InboxQuery = withFilter(emptyQuery(), { kind: 'kind', kinds: ['info'] });

interface BroadcastPanelProps {
  client: StreamChat | null;
  selectedId: ChannelId | null;
  onSelect: (item: InboxItem) => void;
}

export function BroadcastPanel({ client, selectedId, onSelect }: BroadcastPanelProps) {
  const list = useInboxList(INFO_QUERY, client);

  /**
   * Open Jobie on arrival, rather than putting a one-row list beside "Select a
   * conversation" and asking for a click to cross it.
   *
   * Jobie by persona, not by position: the post-login landing is meant to be
   * *that* thread, and taking the newest row would quietly hand the employer a
   * different one the day marketing runs a second campaign. Found in the list
   * this panel already loaded, so nothing has to name the channel — which is
   * what keeps `?tab=info` free of the `chat-mkt-jobie_ajt-emp-<employerId>` id
   * a `?channel=` link would have to carry.
   *
   * Falling back to the newest announcement when Jobie is absent, because an
   * employer who has some other announcement and no Jobie is still better served
   * by reading it than by an empty pane.
   *
   * Once per visit, not whenever nothing is selected: the mobile back button
   * clears the selection to show the list again (`backToList` in `ChatView`),
   * and re-selecting there would put the employer straight back into the thread
   * they just left, with no way out. `ChatLayout` keeps both columns mounted and
   * picks between them in CSS, so this panel survives that round trip and the
   * ref still reads true. Leaving the section unmounts it, which is what makes
   * coming back a fresh arrival.
   *
   * A selection we arrived with (`?tab=info&channel=…`) counts as having landed,
   * so backing out of a deep-linked thread reaches the list too.
   *
   * Safe here in a way it is deliberately not in the inbox — see the note in
   * `WidgetPanel`. Choosing a chat marks it read, and Stream sends the CANDIDATE
   * a receipt, so an unopened message shows as read by someone who never saw it.
   * A broadcast has no counterpart and no reply loop: there is nobody a receipt
   * could reach, and the only thing that moves is the megaphone's own dot, which
   * is correct once the employer is actually looking at the announcement.
   */
  const landed = useRef(false);
  useEffect(() => {
    if (landed.current) return;
    if (selectedId) {
      landed.current = true;
      return;
    }
    const target = list.items.find((i) => isJobie(i.counterpart.id)) ?? list.items[0];
    if (!target) return;
    landed.current = true;
    onSelect(target);
  }, [selectedId, list.items, onSelect]);

  return (
    <div className={styles.panel}>
      <div className={styles.header}>
        <h1 className={styles.title}>Info</h1>
      </div>
      <div className={styles.list}>
        {list.error ? (
          <EmptyState
            icon={<Icon name="megaphone" size={32} />}
            title="Couldn't load messages"
            description="Something went wrong reaching the server."
          />
        ) : list.items.length === 0 && !list.loading ? (
          <EmptyState
            icon={<Icon name="megaphone" size={32} />}
            title="No messages"
            description="Announcements from the platform will appear here."
          />
        ) : (
          list.items.map((item) => (
            <ChannelPreview
              key={item.channel.id}
              item={item}
              active={item.channel.id === selectedId}
              onClick={() => onSelect(item)}
            />
          ))
        )}
      </div>
    </div>
  );
}
